import React, { useRef, useState } from 'react';
import moment from 'moment';
import MuiDateRangePickerDay from '@mui/lab/DateRangePickerDay';
import {
  IconButton,
  InputAdornment,
  TextField,
  Box,
  Popover,
} from '@mui/material';
import { COLOR } from '@src/styles/color';
import { useClickOutside } from '@src/hooks/useClickOutSide';
import DateRangePicker from '@mui/lab/DateRangePicker';
import { vi, enGB } from 'date-fns/locale';
import CustomAdapter from '@src/components/CustomDatePickerRange/CustomDateAdapter';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import ArrowDropUpRoundedIcon from '@mui/icons-material/ArrowDropUpRounded';
import CalendarMonthRounded from '@src/assets/icons/CalendarMonthRounded.svg';
import { LANGUAGE } from '@src/constants';
import i18n from '@src/languages';
import Typography from '../Typography';
import {
  PopoverMenuItem,
  StyledCustomDatePickerRange,
  StyledListMenuItem,
} from './index.style';

const DateRangePickerCustom = ({
  selectedDate = [null, null],
  onChange,
  options,
  format = 'DD/MM/YYYY',
  placeholder = 'DD/MM/YYYY - DD/MM/YYYY',
  shouldDisableDate = () => false,
  width,
  minWidth,
}) => {
  const { language } = i18n;
  const dateRef = useRef(selectedDate?.value);
  const [openDateRangePicker, setOpenDateRangePicker] = useState(false);
  const [anchor, setAnchor] = React.useState(null);
  const popperContainerRef = useClickOutside(() => {
    setAnchor(null);
  });

  const renderWeekPickerDay = (_, dateRangePickerDayProps) => (
    <MuiDateRangePickerDay {...dateRangePickerDayProps} />
  );

  const handleChange = (newValue) => onChange && onChange(newValue);

  const renderDate = (dates) => {
    const startDate = dates[0] ? moment(dates[0]).format(format) : null;
    const endDate = dates[1] ? moment(dates[1]).format(format) : null;

    if (startDate && !endDate) return `${startDate} - ${format}`;
    if (!startDate && endDate) return `${format} - ${endDate}`;
    if (startDate && endDate) return `${startDate} - ${endDate}`;
    return '';
  };

  const handleOpenDateRangePicker = () => setOpenDateRangePicker(true);

  const handleClose = () => {
    setOpenDateRangePicker(false);
    if (dateRef.current && dateRef.current[0] && !dateRef.current[1]) {
      const today = new Date();
      handleChange({
        ...selectedDate,
        value: [dateRef.current[0], today],
      });
      dateRef.current = [dateRef.current[0], today];
    }
  };

  const handleDropdownClick = (event) => {
    setAnchor(event.currentTarget);
  };

  const handleDropdownClose = () => {
    setAnchor(null);
  };

  const handlePresetSelect = (option) => {
    const range = option.getRange();
    handleChange({
      value: range,
      type: option.key,
      label: option.label,
    });
    handleDropdownClose();
    handleClose();
  };

  const renderArrow = (anchorDropdown) => {
    if (anchorDropdown) {
      return (
        <ArrowDropUpRoundedIcon
          sx={{ color: COLOR.navyBlue, fontSize: '24px' }}
        />
      );
    }
    return (
      <ArrowDropDownRoundedIcon
        sx={{ color: COLOR.navyBlue, fontSize: '24px' }}
      />
    );
  };

  return (
    <StyledCustomDatePickerRange width={width} minWidth={minWidth}>
      <LocalizationProvider
        dateAdapter={CustomAdapter}
        localeText={{ start: format, end: format }}
        locale={language === LANGUAGE.VN ? vi : enGB}
      >
        <DateRangePicker
          open={openDateRangePicker}
          calendars={1}
          value={selectedDate?.value}
          onChange={(newValue) => {
            dateRef.current = newValue;
            handleChange({
              value: newValue,
              type: '',
              label: '',
            });
          }}
          onClose={handleClose}
          renderDay={renderWeekPickerDay}
          shouldDisableDate={shouldDisableDate}
          renderInput={(startProps) => (
            <Box display="flex" alignItems="center" width="100%">
              <TextField
                ref={startProps.inputRef}
                {...startProps.inputProps}
                size="small"
                className="text-field"
                value={renderDate(selectedDate?.value)}
                onClick={handleOpenDateRangePicker}
                variant="outlined"
                placeholder={placeholder}
                sx={{
                  '& fieldset': { border: 'none' },
                  '& input': {
                    padding: '0px !important',
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={handleOpenDateRangePicker}
                      >
                        <img src={CalendarMonthRounded} alt="calendar" />
                      </IconButton>
                    </InputAdornment>
                  ),
                  readOnly: true,
                }}
                style={{ flex: 1 }}
              />
              {options?.length > 0 && (
                <>
                  <div
                    ref={popperContainerRef}
                    onClick={handleDropdownClick}
                    role="button"
                    tabIndex={0}
                    style={{ cursor: 'pointer' }}
                    className="dropdown-button"
                  >
                    {renderArrow(anchor)}
                  </div>
                  <Popover
                    open={Boolean(anchor)}
                    anchorEl={anchor}
                    onClose={handleClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    className="popover-date-range-picker"
                    PaperProps={{
                      style: { borderRadius: '8px', width: '280px' },
                    }}
                  >
                    <StyledListMenuItem>
                      {options.map((option) => (
                        <PopoverMenuItem
                          onClick={() => {
                            handlePresetSelect(option);
                            setAnchor(null);
                          }}
                          key={option.label}
                          style={{
                            backgroundColor:
                              selectedDate?.type === option.key &&
                              COLOR.backgroundLinearProgress,
                            borderRadius: '8px',
                          }}
                        >
                          <Typography
                            variant="body-14-medium"
                            color={COLOR.darkGray}
                          >
                            {option.label}
                          </Typography>
                        </PopoverMenuItem>
                      ))}
                    </StyledListMenuItem>
                  </Popover>
                </>
              )}
            </Box>
          )}
        />
      </LocalizationProvider>
    </StyledCustomDatePickerRange>
  );
};
export default DateRangePickerCustom;
